// Components
import ProtectedRoute from './components/common/ProtectedRoute';

// Layouts
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';

// Landing Pages
import Home from './pages/landing/Home';
import JobListings from './pages/landing/JobListings';
import JobDetail from './pages/landing/JobDetail';
import EmployerDirectory from './pages/landing/EmployerDirectory';
import JobSeekerDirectory from './pages/landing/JobSeekerDirectory';
import JobSeekerPublicProfile from './pages/landing/JobSeekerPublicProfile';
import About from './pages/landing/About';
import Contact from './pages/landing/Contact';

// Auth Pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AdminLogin from './components/admin/AdminLogin';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import Jobs from './pages/admin/Jobs';
import ListingDetail from './components/admin/ListingDetail';
import JobEdit from './pages/admin/JobEdit';
import RevenueManagement from './pages/admin/RevenueManagement';
import AdminSettings from './pages/admin/Settings';
import AdminManagement from './pages/admin/AdminManagement';
import BoostRequests from './pages/admin/BoostRequests';
import JobBoosts from './pages/admin/JobBoosts';
import ReviewManagement from './pages/admin/ReviewManagement';
import AuditLogs from './pages/admin/AuditLogs';
import AllJobApplications from './pages/admin/AllJobApplications';
import ContactRequests from './pages/admin/ContactRequests';
import AdsManagement from './pages/admin/AdsManagement';
import Communication from './pages/admin/Communication';
import SMS from './pages/admin/SMS';

// Employer Pages
import EmployerDashboard from './pages/employer/Dashboard';
import PostJob from './pages/employer/PostJob';
import ManageJobs from './pages/employer/ManageJobs';
import ViewApplicants from './pages/employer/ViewApplicants';

// Job Seeker Pages
import JobSeekerDashboard from './pages/jobseeker/Dashboard';
import CreateCV from './pages/jobseeker/CreateCV';
import JobSearch from './pages/jobseeker/JobSearch';
import Applications from './pages/jobseeker/Applications';

// Registration Components
import RegistrationWizard from './components/registration/RegistrationWizard';
import SmartWizard from './components/registration/SmartWizard';

// Common Pages
import CreatePost from './pages/common/CreatePost';
// Legal Pages
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import CookiesPolicy from './pages/legal/CookiesPolicy';

// Profile Management Pages
import JobSeekerProfile from './pages/jobseeker/Profile';
import EmployerProfile from './pages/employer/Profile';

// Profile Redirect Component
import ProfileRedirect from './components/common/ProfileRedirect';

// Settings Pages
import JobSeekerSettings from './pages/jobseeker/Settings';
import EmployerSettings from './pages/employer/Settings';

// Other Pages
import NotFound from './pages/NotFound';

// Routes Configuration
const routes = [
  {
    path: '/',
    element: <MainLayout />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/jobs', element: <JobListings /> },
      { path: '/jobs/:id', element: <JobDetail /> },
      { path: '/jobs/:id/apply', element: <JobDetail /> },
      { path: '/employers', element: <EmployerDirectory /> },
      { path: '/job-seekers', element: <JobSeekerDirectory /> },
      { path: '/job-seekers/:id', element: <JobSeekerPublicProfile /> },
      { path: '/about', element: <About /> },
      { path: '/contact', element: <Contact /> },
      { path: '/privacy', element: <PrivacyPolicy /> },
      { path: '/terms', element: <TermsOfService /> },
      { path: '/cookies', element: <CookiesPolicy /> },
      { path: '/login', element: <Login /> },
      { path: '/admin/login', element: <AdminLogin /> },
      { path: '/register', element: <SmartWizard /> },
      { path: '/register/wizard', element: <RegistrationWizard /> },
      { path: '/wizard/employer', element: <SmartWizard /> },
      { path: '/wizard/job-seeker', element: <SmartWizard /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/reset-password', element: <ResetPassword /> },
      { path: '/create-post', element: <CreatePost /> },
      { path: '/profile', element: <ProfileRedirect /> },
      { path: '*', element: <NotFound /> },
    ],
  },
  {
    path: '/admin',
    element: (
      <ProtectedRoute adminOnly={true}>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: '', element: <AdminDashboard /> },
      { path: 'users', element: <UserManagement /> },
      { path: 'jobs', element: <Jobs /> },
      { path: 'job-applications', element: <AllJobApplications /> },
      { path: 'jobs/:id', element: <ListingDetail type="job" /> },
      { path: 'jobs/:id/edit', element: <JobEdit /> },
      { path: 'revenue', element: <RevenueManagement /> },
      { path: 'boost-requests', element: <BoostRequests /> },
      { path: 'job-boosts', element: <JobBoosts /> },
      { path: 'admin-management', element: <AdminManagement /> },
      { path: 'communications', element: <Communication /> },
      { path: 'communications/:category', element: <Communication /> },
      { path: 'sms', element: <SMS /> },
      { path: 'sms/:category', element: <SMS /> },
      { path: 'review-management', element: <ReviewManagement /> },
      { path: 'audit-logs', element: <AuditLogs /> },
      { path: 'contact-requests', element: <ContactRequests /> },
      { path: 'settings', element: <AdminSettings /> },
      { path: 'ads', element: <AdsManagement /> },
    ],
  },
  {
    path: '/employer',
    element: (
      <ProtectedRoute allowedRoles={['employer']}>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: '', element: <EmployerDashboard /> },
      { path: 'profile', element: <EmployerProfile /> },
      { path: 'post-job', element: <PostJob /> },
      { path: 'jobs/create', element: <PostJob /> },
      { path: 'manage-jobs', element: <ManageJobs /> },
      { path: 'applicants', element: <ViewApplicants /> },
      { path: 'settings', element: <EmployerSettings /> },
    ],
  },
  {
    path: '/jobseeker',
    element: (
      <ProtectedRoute allowedRoles={['jobseeker']}>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: '', element: <JobSeekerDashboard /> },
      { path: 'profile', element: <JobSeekerProfile /> },
      { path: 'create-cv', element: <CreateCV /> },
      { path: 'job-search', element: <JobSearch /> },
      { path: 'applications', element: <Applications /> },
      { path: 'settings', element: <JobSeekerSettings /> },
    ],
  },
];

export default routes;